/**
 * ModificationDetailPanel (KAN-134)
 *
 * Compact inline panel shown when a pending modification is selected.
 * Uses a single table layout: Field | Current | New — no wasted space.
 *
 * Key behaviors:
 *  - "New Value" fields are prepopulated with current values.
 *  - Rollover: Spot + Forward Points = All-In Price (auto-calculated).
 *  - All fields except BankTradeNumber are required (validated on confirm).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Mail } from 'lucide-react';
import { modificationService } from '../../services/api/modificationService';
import { groupingsService } from '../../services/api/groupingsService';
import { clientService } from '../../services/clientService';
import { formatAmountDisplay, formatPriceDisplay, parseAmountInput } from '../../utils/amountFormatter';
import type {
  ModificationTrade,
  ConfirmModificationPayload,
  UnwindCalculationMode,
  UnwindDiscountConvention,
} from '../../types/modification';
import type { GroupingsConfig, GroupingValue } from '../../types/groupings';

interface Props {
  trade: ModificationTrade;
  clientId: string;
  canConfirmReject: boolean;
  onConfirmSuccess: (result: any) => void;
  onRejectSuccess: (reason: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

// Fields that are optional (not validated as required)
const OPTIONAL_FIELDS = new Set([
  'BankTradeNumber', 'HedgeObject',
  // KAN-245: Unwind auto-calculated/derived fields
  'UnwindRate', 'UnwindGrossResult', 'UnwindSettlementAmount',
  'UnwindSettlementDirection', 'UnwindDiscountRate', 'UnwindDiscountConvention',
  'UnwindSpotRate', 'UnwindForwardPoints',
]);

const ModificationDetailPanel: React.FC<Props> = ({
  trade,
  clientId,
  canConfirmReject,
  onConfirmSuccess,
  onRejectSuccess,
  onError,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || 'es';
  const pending = trade.pendingModification;
  const modType = pending?.modificationType || 'Other';

  // ----------------------------------------------------------------
  // Build initial field values from the current trade
  // ----------------------------------------------------------------

  // Convert any date value to YYYY-MM-DD for <input type="date">
  const toDateInputValue = (value: any): string => {
    if (!value) return '';
    const s = String(value).substring(0, 10);
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // DD-MM-YYYY → YYYY-MM-DD
    const ddmmyyyy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
    // DD/MM/YYYY → YYYY-MM-DD
    const slashed = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashed) return `${slashed[3]}-${slashed[2]}-${slashed[1]}`;
    // Try parsing as Date (handles ISO strings, etc.)
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().substring(0, 10);
    return '';
  };

  // Derive default FixingReference from currency pair when switching to NDF.
  // UF (CLF) pairs use UF fixing; all others default to USD_OBS.
  const getDefaultFixingReference = useCallback((): string => {
    const ccy1 = (trade.Currency1 || '').toUpperCase();
    const ccy2 = (trade.Currency2 || '').toUpperCase();
    if (ccy1 === 'CLF' || ccy1 === 'UF' || ccy2 === 'CLF' || ccy2 === 'UF') {
      return 'UF';
    }
    return 'USD_OBS';
  }, [trade.Currency1, trade.Currency2]);

  const buildInitialFields = useCallback((): Record<string, any> => {
    switch (modType) {
      case 'Rollover':
        return {
          MaturityDate: toDateInputValue(trade.MaturityDate),
          SpotPrice: trade.SpotPrice ?? '',
          ForwardPoints: trade.ForwardPoints ?? '',
          Price: trade.Price ?? '',
          BankTradeNumber: '',
        };
      case 'SettlementTypeChange': {
        // Pre-select the OPPOSITE settlement type (that's the whole point of this modification)
        const oppositeType = trade.SettlementType === 'Compensación' ? 'Entrega Física' : 'Compensación';
        const toPhysical = oppositeType === 'Entrega Física';
        return {
          SettlementType: oppositeType,
          // SettlementCurrency and FixingReference are N/A when switching to Entrega Física
          SettlementCurrency: toPhysical ? 'N/A' : (trade.SettlementCurrency || ''),
          FixingReference: toPhysical ? 'N/A' : (trade.FixingReference && trade.FixingReference !== 'N/A' ? trade.FixingReference : getDefaultFixingReference()),
          BankTradeNumber: '',
        };
      }
      case 'GroupingChange':
        return {
          Grouping1: trade.Grouping1 || '',
          Grouping2: trade.Grouping2 || '',
          Grouping3: trade.Grouping3 || '',
          HedgeObject: trade.HedgeObject || '',
          BankTradeNumber: trade.bankTradeNumber || '',
        };
      case 'Unwind':
        return {
          UnwindDate: new Date().toISOString().substring(0, 10),
          UnwindSpotRate: '',
          UnwindForwardPoints: '',
          UnwindRate: '',
          UnwindDiscountRate: '',
          UnwindDiscountConvention: 'None' as UnwindDiscountConvention,
          UnwindGrossResult: '',
          UnwindSettlementAmount: '',
          UnwindSettlementDirection: '',
          UnwindSettlementCurrency: trade.Currency2 || 'CLP',
          UnwindSettlementOverridden: false,
          UnwindCalculationMode: 'Full' as UnwindCalculationMode,
        };
      default:
        return {};
    }
  }, [modType, trade, getDefaultFixingReference]);

  // Form state — prepopulated with current values
  const [modifiedFields, setModifiedFields] = useState<Record<string, any>>(buildInitialFields);
  const [cancellationReason, setCancellationReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // KAN-245: Unwind calculator state
  const [discountEnabled, setDiscountEnabled] = useState(false);
  // Tracks the auto-calculated settlement amount for strikethrough display
  const [calculatedSettlement, setCalculatedSettlement] = useState<number | null>(null);
  // Local string states for unwind inputs — same pattern as TradeDetailsSection.tsx
  // User types freely; on blur we parse + reformat + push to modifiedFields
  const [uwSpotInput, setUwSpotInput] = useState('');
  const [uwFwdPtsInput, setUwFwdPtsInput] = useState('');
  const [uwDiscountRateInput, setUwDiscountRateInput] = useState('');
  const [uwSettlementInput, setUwSettlementInput] = useState('');

  // KAN-245: Settlement account state for Unwind
  const [uwBankAccounts, setUwBankAccounts] = useState<Array<{ id: string; bankName: string; accountNumber: string; accountCurrency: string; accountType?: string; counterpartyId?: string }>>([]);
  const [uwAccountSelections, setUwAccountSelections] = useState<{ cargarAccountId?: string; abonarAccountId?: string }>({});

  // Load groupings config for GroupingChange modifications
  useEffect(() => {
    if (modType !== 'GroupingChange') return;
    groupingsService.getGroupings(clientId).then(config => {
      if (config) setGroupingsConfig(config);
    }).catch(() => { /* fall back to free text inputs */ });
  }, [clientId, modType]);

  // KAN-245: Load settlement account preview for Unwind
  useEffect(() => {
    if (modType !== 'Unwind') return;
    const tradeNumber = trade.TradeNumber;
    if (!tradeNumber) return;
    clientService.previewSettlementAccounts(clientId, [tradeNumber]).then(resp => {
      if (resp.success && resp.data) {
        setUwBankAccounts(resp.data.bankAccounts);
        const preview = resp.data.previews?.[0];
        if (preview?.ruleMatched) {
          setUwAccountSelections({
            cargarAccountId: preview.cargarAccountId,
            abonarAccountId: preview.abonarAccountId,
          });
        }
      }
    }).catch(() => { /* non-critical */ });
  }, [clientId, modType, trade.TradeNumber]);

  // Reset form when trade changes
  useEffect(() => {
    setModifiedFields(buildInitialFields());
    setCancellationReason('');
    setRejectReason('');
    setShowRejectForm(false);
    setDiscountEnabled(false);
    setCalculatedSettlement(null);
    setUwSpotInput('');
    setUwFwdPtsInput('');
    setUwDiscountRateInput('');
    setUwSettlementInput('');
    setUwAccountSelections({});
  }, [trade.id, buildInitialFields]);

  const updateField = useCallback((field: string, value: any) => {
    setModifiedFields(prev => {
      const next = { ...prev, [field]: value };

      // Auto-calculate All-In Price when Spot or Forward Points change (Rollover only)
      if (modType === 'Rollover' && (field === 'SpotPrice' || field === 'ForwardPoints')) {
        const spot = field === 'SpotPrice' ? value : next.SpotPrice;
        const fwd = field === 'ForwardPoints' ? value : next.ForwardPoints;
        const spotNum = Number(spot);
        const fwdNum = Number(fwd);
        if (!isNaN(spotNum) && !isNaN(fwdNum) && spot !== '' && fwd !== '') {
          next.Price = Math.round((spotNum + fwdNum) * 100) / 100;
        }
      }

      // Auto-set dependent fields when SettlementType changes (SettlementTypeChange only)
      if (modType === 'SettlementTypeChange' && field === 'SettlementType') {
        if (value === 'Entrega Física') {
          next.SettlementCurrency = 'N/A';
          next.FixingReference = 'N/A';
        } else if (value === 'Compensación') {
          next.SettlementCurrency = trade.SettlementCurrency || '';
          next.FixingReference = trade.FixingReference && trade.FixingReference !== 'N/A' ? trade.FixingReference : getDefaultFixingReference();
        }
      }

      // Clear dependent groupings in subgrouping mode (GroupingChange only)
      if (modType === 'GroupingChange' && groupingsConfig) {
        if (field === 'Grouping1' && groupingsConfig.grouping2Mode === 'subgrouping') {
          next.Grouping2 = '';
          next.Grouping3 = '';
        }
        if (field === 'Grouping2' && groupingsConfig.grouping3Mode === 'subgrouping') {
          next.Grouping3 = '';
        }
      }

      // KAN-245: Unwind live calculation — three-way interdependence:
      //   Spot + ForwardPoints = AllIn Rate
      //   Any two entered → third is derived
      if (modType === 'Unwind' && field !== 'UnwindSettlementAmount') {
        const originalPrice = Number(trade.Price) || 0;
        const qty1 = Number(trade.QuantityCurrency1) || 0;
        const isBuy = trade.Direction === 'Buy';

        // Three-way field interdependence: Spot + FwdPts = AllIn
        const spot = Number(next.UnwindSpotRate);
        const fwd = Number(next.UnwindForwardPoints);
        const allIn = Number(next.UnwindRate);
        const hasSpot = next.UnwindSpotRate !== '' && !isNaN(spot);
        const hasFwd = next.UnwindForwardPoints !== '' && !isNaN(fwd);
        const hasAllIn = next.UnwindRate !== '' && !isNaN(allIn);

        // AllIn is always derived from Spot + FwdPts (read-only display)
        if (field === 'UnwindSpotRate' || field === 'UnwindForwardPoints') {
          if (hasSpot && hasFwd) {
            next.UnwindRate = Math.round((spot + fwd) * 1e6) / 1e6;
          }
        }

        // Recalculate GrossResult and Settlement from the effective AllIn rate
        const effectiveRate = Number(next.UnwindRate);
        if (!isNaN(effectiveRate) && next.UnwindRate !== '' && qty1 !== 0) {
          const gross = isBuy
            ? (effectiveRate - originalPrice) * qty1
            : (originalPrice - effectiveRate) * qty1;
          next.UnwindGrossResult = Math.round(gross * 100) / 100;

          // Calculate settlement (with optional discounting)
          // User enters discount rate as a percentage (e.g. 5 for 5%), convert to decimal
          const discountRatePct = Number(next.UnwindDiscountRate);
          const discountRateDecimal = discountRatePct / 100;
          const convention = next.UnwindDiscountConvention || 'None';
          let settlement = gross;
          if (convention !== 'None' && !isNaN(discountRateDecimal) && discountRateDecimal !== 0 && next.UnwindDate && trade.MaturityDate) {
            const uwDate = new Date(next.UnwindDate + 'T00:00:00');
            const matDate = new Date(String(trade.MaturityDate).substring(0, 10) + 'T00:00:00');
            const residualDays = Math.round((matDate.getTime() - uwDate.getTime()) / 86400000);
            if (residualDays > 0) {
              const divisor = convention === '30-day' ? 30 : 360;
              settlement = gross / Math.pow(1 + discountRateDecimal, residualDays / divisor);
            }
          }
          const calcAmount = Math.round(settlement * 100) / 100;
          next.UnwindSettlementAmount = calcAmount;
          next.UnwindSettlementDirection = gross >= 0 ? 'Receivable' : 'Payable';
          // Track calculated value for strikethrough comparison
          setCalculatedSettlement(calcAmount);
          // Update settlement display string (0 decimals for CLP-like currencies)
          const settlementDecimals = ['CLP', 'JPY', 'KRW'].includes(trade.Currency2 || '') ? 0 : 2;
          setUwSettlementInput(formatAmountDisplay(calcAmount, locale, settlementDecimals));
        }
      }

      return next;
    });
  }, [modType, trade, groupingsConfig, locale]);

  // KAN-245: Simple rate parser — for Spot, FwdPts, DiscountRate.
  // Rates/prices NEVER have thousands separators, so treat both comma and dot as decimal.
  const parseRateValue = (input: string): number | null => {
    if (!input || input.trim() === '') return null;
    const normalized = input.trim().replace(',', '.');
    if (!/^-?\d*\.?\d+$/.test(normalized)) return null;
    const num = parseFloat(normalized);
    return isNaN(num) ? null : num;
  };

  // Blur handler for rate fields (Spot, FwdPts) — uses simple parser, no thousands heuristic
  const handleUnwindBlur = (field: string, rawValue: string, setDisplay: (s: string) => void) => {
    const parsed = parseRateValue(rawValue);
    if (parsed !== null) {
      updateField(field, parsed);
      // Reformat with locale-aware display (prices use smart decimals)
      setDisplay(formatPriceDisplay(parsed, locale, 6));
    } else if (rawValue.trim() === '') {
      updateField(field, '');
      setDisplay('');
    }
    // If parse fails and non-empty, leave user's text as-is (they can correct it)
  };

  const handleDiscountRateBlur = () => {
    const parsed = parseRateValue(uwDiscountRateInput);
    if (parsed !== null) {
      updateField('UnwindDiscountRate', parsed);
      setUwDiscountRateInput(formatPriceDisplay(parsed, locale, 4));
    } else if (uwDiscountRateInput.trim() === '') {
      updateField('UnwindDiscountRate', '');
      setUwDiscountRateInput('');
    }
  };

  const handleSettlementBlur = () => {
    const parsed = parseAmountInput(uwSettlementInput, locale);
    if (parsed !== null) {
      setModifiedFields(prev => ({
        ...prev,
        UnwindSettlementAmount: parsed,
        UnwindSettlementOverridden: parsed !== calculatedSettlement,
        UnwindSettlementDirection: parsed >= 0 ? 'Receivable' : 'Payable',
      }));
      const settlementDecimals = ['CLP', 'JPY', 'KRW'].includes(trade.Currency2 || '') ? 0 : 2;
      setUwSettlementInput(formatAmountDisplay(parsed, locale, settlementDecimals));
    } else if (uwSettlementInput.trim() === '') {
      setModifiedFields(prev => ({
        ...prev,
        UnwindSettlementAmount: '',
        UnwindSettlementOverridden: false,
        UnwindSettlementDirection: '',
      }));
      setUwSettlementInput('');
    }
  };

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (value: any): string => {
    if (!value) return '-';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  // Modification type label key
  const modTypeKey = modType === 'SettlementTypeChange'
    ? 'settlementTypeChange'
    : modType.charAt(0).toLowerCase() + modType.slice(1);

  // ----------------------------------------------------------------
  // Validation
  // ----------------------------------------------------------------

  const validateFields = (): string[] => {
    const missing: string[] = [];
    for (const f of getFields()) {
      if (OPTIONAL_FIELDS.has(f.key)) continue;
      const val = modifiedFields[f.key];
      if (val == null || val === '' || (typeof val === 'number' && isNaN(val))) {
        missing.push(f.label);
      }
    }
    return missing;
  };

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------

  const handleConfirm = async () => {
    if (modType === 'Cancellation') {
      if (!cancellationReason.trim()) {
        onError(t('modifications.detail.cancellationReasonRequired'));
        return;
      }
    }

    // Validate required fields for non-Cancellation / non-Other / non-Unwind types
    if (modType !== 'Cancellation' && modType !== 'Other' && modType !== 'Unwind') {
      const missing = validateFields();
      if (missing.length > 0) {
        onError(t('modifications.messages.validationRequired', { fields: missing.join(', ') }));
        return;
      }
    }

    // KAN-245: Validate unwind has minimum required values
    if (modType === 'Unwind') {
      if (!modifiedFields.UnwindRate && modifiedFields.UnwindRate !== 0) {
        onError(t('modifications.messages.validationRequired', { fields: t('modifications.fields.unwindRate') }));
        return;
      }
      if (!modifiedFields.UnwindSettlementAmount && modifiedFields.UnwindSettlementAmount !== 0) {
        onError(t('modifications.messages.validationRequired', { fields: t('modifications.fields.unwindSettlementAmount') }));
        return;
      }
    }

    // Validate maturity date is not in the past (Rollover only)
    if (modType === 'Rollover' && modifiedFields.MaturityDate) {
      const todayStr = new Date().toISOString().substring(0, 10);
      if (modifiedFields.MaturityDate < todayStr) {
        onError(t('modifications.messages.maturityDatePast'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: ConfirmModificationPayload = {};
      if (modType === 'Cancellation') {
        payload.cancellationReason = cancellationReason.trim();
      } else if (modType === 'SettlementTypeChange' || modType === 'Unwind') {
        // Always send all fields — Unwind fields are all new (not a diff), and
        // SettlementTypeChange pre-selects the opposite type.
        if (Object.keys(modifiedFields).length > 0) {
          payload.modifiedFields = { ...modifiedFields };
          // KAN-245: Include selected settlement accounts for SI generation on confirmation
          if (modType === 'Unwind') {
            if (modifiedFields.UnwindSettlementDirection === 'Payable' && uwAccountSelections.cargarAccountId) {
              payload.modifiedFields.UnwindCargarAccountId = uwAccountSelections.cargarAccountId;
              const acc = uwBankAccounts.find(a => a.id === uwAccountSelections.cargarAccountId);
              if (acc) payload.modifiedFields.UnwindAccountName = `${acc.bankName} - ${acc.accountNumber}`;
            }
            if (modifiedFields.UnwindSettlementDirection === 'Receivable' && uwAccountSelections.abonarAccountId) {
              payload.modifiedFields.UnwindAbonarAccountId = uwAccountSelections.abonarAccountId;
              const acc = uwBankAccounts.find(a => a.id === uwAccountSelections.abonarAccountId);
              if (acc) payload.modifiedFields.UnwindAccountName = `${acc.bankName} - ${acc.accountNumber}`;
            }
          }
        }
      } else if (Object.keys(modifiedFields).length > 0) {
        // Only send fields that actually changed from original values
        const changed: Record<string, any> = {};
        const initial = buildInitialFields();
        for (const [key, value] of Object.entries(modifiedFields)) {
          if (String(value) !== String(initial[key] ?? '')) {
            changed[key] = value;
          }
        }
        if (Object.keys(changed).length > 0) {
          payload.modifiedFields = changed;
        }
      }

      const response = await modificationService.confirmModification(
        clientId, trade.id, payload, trade.OurPartyId
      );
      if (response.success) {
        // Build changed-fields summary for the result modal
        const initial = buildInitialFields();
        const changedSummary: { label: string; from: string; to: string }[] = [];
        // Convert YYYY-MM-DD back to DD-MM-YYYY for display
        const toDisplayDate = (v: string) => {
          const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
        };
        if (modType === 'Unwind') {
          // Unwind uses its own layout — build summary from user-visible fields only
          const settlementCcy = modifiedFields.UnwindSettlementCurrency || trade.Currency2 || '';
          const settlementDp = ['CLP', 'JPY', 'KRW'].includes(settlementCcy) ? 0 : 2;
          const fmtDate = (v: string) => {
            const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
          };
          const fmtVal = (key: string, val: any): string => {
            const s = String(val);
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fmtDate(s);
            if (key === 'UnwindSettlementAmount') {
              return `${formatAmountDisplay(Number(val), locale, settlementDp)} ${settlementCcy}`;
            }
            if (key === 'UnwindDiscountRate') {
              return `${formatPriceDisplay(Number(val), locale, 4)}%`;
            }
            if (key === 'UnwindSettlementDirection') {
              return val === 'Receivable' ? t('modifications.unwind.weReceive') : t('modifications.unwind.wePay');
            }
            if (['UnwindSpotRate', 'UnwindForwardPoints', 'UnwindRate'].includes(key)) {
              return formatPriceDisplay(Number(val), locale, 6);
            }
            return s;
          };
          const unwindVisibleFields = [
            { key: 'UnwindDate', label: t('modifications.fields.unwindDate') },
            { key: 'UnwindSpotRate', label: t('modifications.fields.unwindSpotRate') },
            { key: 'UnwindForwardPoints', label: t('modifications.fields.unwindForwardPoints') },
            { key: 'UnwindRate', label: t('modifications.fields.unwindRate') },
            { key: 'UnwindDiscountRate', label: t('modifications.fields.unwindDiscountRate') },
            { key: 'UnwindSettlementAmount', label: t('modifications.fields.unwindSettlementAmount') },
            { key: 'UnwindSettlementDirection', label: t('modifications.fields.unwindSettlementDirection') },
          ];
          for (const f of unwindVisibleFields) {
            const val = modifiedFields[f.key];
            if (val != null && val !== '') {
              changedSummary.push({ label: f.label, from: '', to: fmtVal(f.key, val) });
            }
          }
          // Append the selected settlement account (only the relevant side)
          if (modifiedFields.UnwindSettlementDirection === 'Payable' && uwAccountSelections.cargarAccountId) {
            const acc = uwBankAccounts.find(a => a.id === uwAccountSelections.cargarAccountId);
            if (acc) changedSummary.push({ label: t('modifications.unwind.cargarAccount'), from: '', to: `${acc.bankName} - ${acc.accountNumber}` });
          }
          if (modifiedFields.UnwindSettlementDirection === 'Receivable' && uwAccountSelections.abonarAccountId) {
            const acc = uwBankAccounts.find(a => a.id === uwAccountSelections.abonarAccountId);
            if (acc) changedSummary.push({ label: t('modifications.unwind.abonarAccount'), from: '', to: `${acc.bankName} - ${acc.accountNumber}` });
          }
        } else {
          for (const f of getFields()) {
            // For SettlementTypeChange compare against trade current values (not form-initial),
            // because form-initial is the opposite type and would falsely show no changes.
            const oldVal = modType === 'SettlementTypeChange'
              ? String(trade[f.key] ?? '')
              : String(initial[f.key] ?? '');
            const newVal = String(modifiedFields[f.key] ?? '');
            if (oldVal !== newVal) {
              const displayTo = /^\d{4}-\d{2}-\d{2}$/.test(newVal) ? toDisplayDate(newVal) : newVal;
              changedSummary.push({ label: f.label, from: f.current, to: displayTo });
            }
          }
        }
        onConfirmSuccess({ ...response.data, changedFieldsSummary: changedSummary });
      } else {
        const msg = response.message || '';
        onError(msg.includes('MAKER_CHECKER_VIOLATION') ? t('modifications.messages.makerCheckerViolation') : msg || t('modifications.messages.confirmError'));
      }
    } catch (err: any) {
      const msg = err.message || '';
      onError(msg.includes('MAKER_CHECKER_VIOLATION') ? t('modifications.messages.makerCheckerViolation') : msg || t('modifications.messages.confirmError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      onError(t('modifications.detail.rejectReasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await modificationService.rejectModification(
        clientId, trade.id, { reason: rejectReason.trim() }, trade.OurPartyId
      );
      if (response.success) {
        onRejectSuccess(rejectReason.trim());
      } else {
        onError(response.message || t('modifications.messages.rejectError'));
      }
    } catch (err: any) {
      onError(err.message || t('modifications.messages.rejectError'));
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------------------------------------------
  // Grouping options helper (GroupingChange only)
  // ----------------------------------------------------------------

  const getGroupingOptions = (groupingNumber: 1 | 2 | 3): GroupingValue[] => {
    if (!groupingsConfig) return [];
    const entityMapping = groupingsConfig.entityMappings.find(
      m => m.entityId === trade.OurPartyId
    );
    // No entity mapping found — show all active values for this grouping
    if (!entityMapping) {
      const all = groupingNumber === 1 ? groupingsConfig.grouping1Values
                : groupingNumber === 2 ? groupingsConfig.grouping2Values
                : groupingsConfig.grouping3Values;
      return all.filter(v => v.isActive);
    }
    if (groupingNumber === 1) {
      return groupingsConfig.grouping1Values.filter(
        v => v.isActive && entityMapping.grouping1ValueIds.includes(v.id)
      );
    }
    if (groupingNumber === 2) {
      if (groupingsConfig.grouping2Mode === 'subgrouping') {
        const selectedG1 = groupingsConfig.grouping1Values.find(v => v.name === modifiedFields.Grouping1);
        if (!selectedG1 || !entityMapping.g1ToG2Mappings) return [];
        const allowed = entityMapping.g1ToG2Mappings[selectedG1.id] || [];
        return groupingsConfig.grouping2Values.filter(v => v.isActive && allowed.includes(v.id));
      }
      return groupingsConfig.grouping2Values.filter(
        v => v.isActive && entityMapping.grouping2ValueIds.includes(v.id)
      );
    }
    // groupingNumber === 3
    if (groupingsConfig.grouping3Mode === 'subgrouping') {
      const selectedG2 = groupingsConfig.grouping2Values.find(v => v.name === modifiedFields.Grouping2);
      if (!selectedG2 || !entityMapping.g2ToG3Mappings) return [];
      const allowed = entityMapping.g2ToG3Mappings[selectedG2.id] || [];
      return groupingsConfig.grouping3Values.filter(v => v.isActive && allowed.includes(v.id));
    }
    return groupingsConfig.grouping3Values.filter(
      v => v.isActive && entityMapping.grouping3ValueIds.includes(v.id)
    );
  };

  // ----------------------------------------------------------------
  // Field definitions per modification type
  // ----------------------------------------------------------------

  type FieldDef = {
    key: string;
    label: string;
    current: string;
    input: React.ReactNode;
  };

  const rolloverFields: FieldDef[] = [
    {
      key: 'MaturityDate', label: t('modifications.fields.maturityDate'),
      current: formatDate(trade.MaturityDate),
      input: <input type="date" className="mod-panel-input" value={modifiedFields.MaturityDate || ''} onChange={(e) => updateField('MaturityDate', e.target.value)} />,
    },
    {
      key: 'SpotPrice', label: t('modifications.fields.spotPrice'),
      current: formatNumber(trade.SpotPrice),
      input: <input type="number" step="0.01" className="mod-panel-input" value={modifiedFields.SpotPrice ?? ''} onChange={(e) => updateField('SpotPrice', e.target.value ? Number(e.target.value) : '')} />,
    },
    {
      key: 'ForwardPoints', label: t('modifications.fields.forwardPoints'),
      current: formatNumber(trade.ForwardPoints),
      input: <input type="number" step="0.01" className="mod-panel-input" value={modifiedFields.ForwardPoints ?? ''} onChange={(e) => updateField('ForwardPoints', e.target.value ? Number(e.target.value) : '')} />,
    },
    {
      key: 'Price', label: t('modifications.fields.price'),
      current: formatNumber(trade.Price),
      input: (
        <input
          type="number"
          step="0.01"
          className="mod-panel-input mod-panel-input-calculated"
          value={modifiedFields.Price ?? ''}
          onChange={(e) => updateField('Price', e.target.value ? Number(e.target.value) : '')}
          title={t('modifications.messages.priceAutoCalc')}
        />
      ),
    },
    {
      key: 'BankTradeNumber', label: t('modifications.fields.bankTradeNumber'),
      current: trade.bankTradeNumber || '-',
      input: <input type="text" className="mod-panel-input" value={modifiedFields.BankTradeNumber || ''} onChange={(e) => updateField('BankTradeNumber', e.target.value)} />,
    },
  ];

  const settlementFields: FieldDef[] = [
    {
      key: 'SettlementType', label: t('modifications.fields.settlementType'),
      current: trade.SettlementType || '-',
      input: (
        <select className="mod-panel-select" value={modifiedFields.SettlementType || ''} onChange={(e) => updateField('SettlementType', e.target.value)}>
          <option value="">{t('common.select')}</option>
          <option value="Compensación">Compensación</option>
          <option value="Entrega Física">Entrega Física</option>
        </select>
      ),
    },
    {
      key: 'SettlementCurrency', label: t('modifications.fields.settlementCurrency'),
      current: trade.SettlementCurrency || '-',
      input: (
        <select className="mod-panel-select" value={modifiedFields.SettlementCurrency || ''} onChange={(e) => updateField('SettlementCurrency', e.target.value)}>
          <option value="">{t('common.select')}</option>
          <option value="CLP">CLP</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      ),
    },
    {
      key: 'FixingReference', label: t('modifications.fields.fixingReference'),
      current: trade.FixingReference ? t(`tradeBooking.fixingReferences.${trade.FixingReference}`, { defaultValue: trade.FixingReference }) : '-',
      input: (
        <select className="mod-panel-select" value={modifiedFields.FixingReference || ''} onChange={(e) => updateField('FixingReference', e.target.value)}>
          <option value="">{t('common.select')}</option>
          <option value="USD_OBS">{t('tradeBooking.fixingReferences.USD_OBS')}</option>
          <option value="UF">{t('tradeBooking.fixingReferences.UF')}</option>
          <option value="BLOOMBERG_11AM">{t('tradeBooking.fixingReferences.BLOOMBERG_11AM')}</option>
        </select>
      ),
    },
    {
      key: 'BankTradeNumber', label: t('modifications.fields.bankTradeNumber'),
      current: trade.bankTradeNumber || '-',
      input: <input type="text" className="mod-panel-input" value={modifiedFields.BankTradeNumber || ''} onChange={(e) => updateField('BankTradeNumber', e.target.value)} />,
    },
  ];

  // Grouping fields — labels and options from client config; fall back to text inputs if not loaded.
  // When config isn't loaded yet, still show G2/G3 rows if the trade already has values for them.
  const g1Label = groupingsConfig?.grouping1Label || t('modifications.fields.grouping1');
  const g2Label = groupingsConfig?.grouping2Label || (trade.Grouping2 ? t('modifications.fields.grouping2') : '');
  const g3Label = groupingsConfig?.grouping3Label || (trade.Grouping3 ? t('modifications.fields.grouping3') : '');

  const groupingFields: FieldDef[] = [
    {
      key: 'Grouping1', label: g1Label,
      current: trade.Grouping1 || '-',
      input: groupingsConfig ? (
        <select className="mod-panel-select" value={modifiedFields.Grouping1 || ''} onChange={(e) => updateField('Grouping1', e.target.value)}>
          <option value="">{t('common.select')}</option>
          {getGroupingOptions(1).map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
        </select>
      ) : (
        <input type="text" className="mod-panel-input" value={modifiedFields.Grouping1 || ''} onChange={(e) => updateField('Grouping1', e.target.value)} />
      ),
    },
    // Grouping 2 — only rendered when the client has it configured
    ...(g2Label ? [{
      key: 'Grouping2', label: g2Label,
      current: trade.Grouping2 || '-',
      input: (
        <select
          className="mod-panel-select"
          value={modifiedFields.Grouping2 || ''}
          onChange={(e) => updateField('Grouping2', e.target.value)}
          disabled={groupingsConfig?.grouping2Mode === 'subgrouping' && !modifiedFields.Grouping1}
        >
          <option value="">{t('common.select')}</option>
          {getGroupingOptions(2).map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
        </select>
      ),
    }] as FieldDef[] : []),
    // Grouping 3 — only rendered when the client has it configured
    ...(g3Label ? [{
      key: 'Grouping3', label: g3Label,
      current: trade.Grouping3 || '-',
      input: (
        <select
          className="mod-panel-select"
          value={modifiedFields.Grouping3 || ''}
          onChange={(e) => updateField('Grouping3', e.target.value)}
          disabled={groupingsConfig?.grouping3Mode === 'subgrouping' && !modifiedFields.Grouping2}
        >
          <option value="">{t('common.select')}</option>
          {getGroupingOptions(3).map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
        </select>
      ),
    }] as FieldDef[] : []),
    {
      key: 'HedgeObject', label: t('modifications.fields.hedgeObject'),
      current: trade.HedgeObject || '-',
      input: <input type="text" className="mod-panel-input" value={modifiedFields.HedgeObject || ''} onChange={(e) => updateField('HedgeObject', e.target.value)} />,
    },
    {
      key: 'BankTradeNumber', label: t('modifications.fields.bankTradeNumber'),
      current: trade.bankTradeNumber || '-',
      input: <input type="text" className="mod-panel-input" value={modifiedFields.BankTradeNumber || ''} onChange={(e) => updateField('BankTradeNumber', e.target.value)} />,
    },
  ];

  // KAN-245: Unwind calculator helper — residual days
  const unwindResidualDays = (() => {
    if (!modifiedFields.UnwindDate || !trade.MaturityDate) return 0;
    const uwDate = new Date(modifiedFields.UnwindDate + 'T00:00:00');
    const matDate = new Date(String(trade.MaturityDate).substring(0, 10) + 'T00:00:00');
    return Math.round((matDate.getTime() - uwDate.getTime()) / 86400000);
  })();

  const getFields = (): FieldDef[] => {
    switch (modType) {
      case 'Rollover': return rolloverFields;
      case 'SettlementTypeChange': return settlementFields;
      case 'GroupingChange': return groupingFields;
      // Unwind uses its own vertical form layout — not the table
      default: return [];
    }
  };

  const buildMailtoUrl = (): string => {
    const ccyPair = `${trade.Currency1 || ''}/${trade.Currency2 || ''}`;
    const amountStr = trade.QuantityCurrency1 != null
      ? trade.QuantityCurrency1.toLocaleString('en-US')
      : '';
    const bankRef = trade.bankTradeNumber || '';

    // Subject
    const subjectPrefix = modType === 'Rollover' ? t('modifications.mail.subjectRollover')
      : modType === 'SettlementTypeChange' ? t('modifications.mail.subjectSettlement')
      : t('modifications.mail.subjectCancellation');
    const subjectParts = [subjectPrefix];
    if (bankRef) subjectParts.push(`${t('modifications.mail.subjectBankRefLabel')} ${bankRef}`);
    subjectParts.push(`${ccyPair} ${amountStr}`);
    if (modType === 'Rollover') subjectParts.push(formatDate(trade.MaturityDate));
    const subject = subjectParts.join(' – ');

    const intro = modType === 'Rollover' ? t('modifications.mail.introRollover')
      : modType === 'SettlementTypeChange' ? t('modifications.mail.introSettlement')
      : t('modifications.mail.introCancellation');

    const lbl = (key: string) => `${t(key)}:`;

    // SettlementTypeChange — show only references + settlement change details
    if (modType === 'SettlementTypeChange') {
      const lines: string[] = [
        t('modifications.mail.greeting', { name: trade.CounterpartyName || '' }),
        '',
        intro,
        '',
        `${lbl('modifications.mail.bankTradeNumber')}  ${bankRef}`,
        `${lbl('modifications.mail.ourReference')}  ${trade.TradeNumber}`,
        `${lbl('modifications.mail.currencyPair')}  ${ccyPair}`,
        '',
        `${lbl('modifications.mail.currentSettlementType')}  ${trade.SettlementType || '-'}`,
        `${lbl('modifications.mail.newSettlementType')}  ${modifiedFields.SettlementType || '-'}`,
      ];
      if (modifiedFields.SettlementType === 'Compensación') {
        const sc = modifiedFields.SettlementCurrency;
        const fr = modifiedFields.FixingReference;
        if (sc && sc !== 'N/A') lines.push(`${lbl('modifications.mail.settlementCurrency')}  ${sc}`);
        if (fr && fr !== 'N/A') lines.push(`${lbl('modifications.mail.fixingReference')}  ${fr}`);
      }
      lines.push(`${lbl('modifications.mail.entity')}  ${trade.OurParty || ''}`);
      lines.push('');
      lines.push(t('modifications.mail.closingGeneral'));
      lines.push('');
      lines.push(trade.OurParty || '');
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    }

    // Rollover / Cancellation — generic trade details
    const lines: string[] = [
      t('modifications.mail.greeting', { name: trade.CounterpartyName || '' }),
      '',
      intro,
      '',
      `${lbl('modifications.mail.bankTradeNumber')}  ${bankRef}`,
      `${lbl('modifications.mail.ourReference')}  ${trade.TradeNumber}`,
      `${lbl('modifications.mail.currencyPair')}  ${ccyPair}`,
      `${lbl('modifications.columns.amount')}  ${amountStr} ${trade.Currency1 || ''}`,
      `${lbl('modifications.columns.direction')}  ${trade.Direction || '-'}`,
      `${lbl('modifications.mail.currentMaturity')}  ${formatDate(trade.MaturityDate)}`,
    ];

    // Proposed new maturity (Rollover only — use whatever the FO has entered in the form)
    if (modType === 'Rollover' && modifiedFields.MaturityDate) {
      const raw = String(modifiedFields.MaturityDate);
      const mm = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      lines.push(`${lbl('modifications.mail.newMaturity')}  ${mm ? `${mm[3]}/${mm[2]}/${mm[1]}` : raw}`);
    }

    lines.push(`${lbl('modifications.mail.entity')}  ${trade.OurParty || ''}`);

    lines.push('');
    lines.push(modType === 'Rollover'
      ? t('modifications.mail.closingRollover')
      : t('modifications.mail.closingGeneral'));
    lines.push('');
    lines.push(trade.OurParty || '');

    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
  };

  const confirmLabel = modType === 'Cancellation'
    ? t('modifications.detail.confirmCancellation')
    : modType === 'Other'
      ? t('modifications.detail.approveOther')
      : t('modifications.detail.confirmAndExecute');

  const canSubmitConfirm = modType === 'Cancellation'
    ? cancellationReason.trim().length > 0
    : true;

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  const fields = getFields();

  return (
    <div className="mod-panel">
      {/* Header bar */}
      <div className="mod-panel-header">
        <div className="mod-panel-header-left">
          <div className="mod-panel-header-row">
            <span className="mod-panel-trade-num">
              {t('modifications.detail.title', { tradeNumber: trade.TradeNumber })}
            </span>
            <span className="mod-panel-mod-type">
              {t(`modifications.types.${modTypeKey}`)}
            </span>
          </div>
          {pending && (
            <span className="mod-panel-meta">
              {t('modifications.detail.initiatedBy', {
                name: pending.initiatedByName,
                date: formatDate(pending.initiatedAt),
              })}
            </span>
          )}
          {pending?.comment && (
            <span className="mod-panel-comment">"{pending.comment}"</span>
          )}
        </div>
        <div className="mod-panel-header-actions">
          {modType !== 'GroupingChange' && modType !== 'Other' && modType !== 'Unwind' && (
            <a
              href={buildMailtoUrl()}
              className="mod-panel-mail-btn"
              title={t('modifications.mail.tooltip')}
            >
              <Mail size={16} />
            </a>
          )}
          <button className="mod-panel-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Form content — hidden when in reject mode */}
      {!showRejectForm && (
        <>
          {/* KAN-245: Unwind two-column table form — type="text" with parseAmountInput on blur */}
          {modType === 'Unwind' && (() => {
            const settlementDecimals = ['CLP', 'JPY', 'KRW'].includes(trade.Currency2 || '') ? 0 : 2;
            return (
            <table className="unwind-table">
              <tbody>
                {/* Notional Amount (read-only) */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.unwind.notionalLabel')}</td>
                  <td className="unwind-table-value unwind-table-readonly">
                    {formatAmountDisplay(Number(trade.QuantityCurrency1), locale, ['CLP', 'JPY', 'KRW'].includes(trade.Currency1 || '') ? 0 : 2)} {trade.Currency1}
                  </td>
                </tr>
                {/* Original Maturity Date (read-only) */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.fields.maturityDate')}</td>
                  <td className="unwind-table-value unwind-table-readonly">
                    {formatDate(trade.MaturityDate)}
                  </td>
                </tr>
                {/* Unwind Date — capped at original maturity (early termination must be before maturity) */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.fields.unwindDate')}</td>
                  <td className="unwind-table-value">
                    <input
                      type="date"
                      value={modifiedFields.UnwindDate || ''}
                      max={toDateInputValue(trade.MaturityDate)}
                      onChange={(e) => {
                        const maxDate = toDateInputValue(trade.MaturityDate);
                        const val = e.target.value;
                        // Clamp manually-typed dates that exceed maturity
                        updateField('UnwindDate', maxDate && val > maxDate ? maxDate : val);
                      }}
                    />
                  </td>
                </tr>
                {/* Original Forward Price (read-only) */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.unwind.originalPrice')}</td>
                  <td className="unwind-table-value unwind-table-readonly">
                    {formatPriceDisplay(Number(trade.Price), locale, 4)}
                  </td>
                </tr>
                {/* Spot Rate */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.unwind.spotLabel')}</td>
                  <td className="unwind-table-value">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={uwSpotInput}
                      onChange={(e) => setUwSpotInput(e.target.value)}
                      onBlur={() => handleUnwindBlur('UnwindSpotRate', uwSpotInput, setUwSpotInput)}
                    />
                  </td>
                </tr>
                {/* Forward Points */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.unwind.fwdPtsLabel')}</td>
                  <td className="unwind-table-value">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={uwFwdPtsInput}
                      onChange={(e) => setUwFwdPtsInput(e.target.value)}
                      onBlur={() => handleUnwindBlur('UnwindForwardPoints', uwFwdPtsInput, setUwFwdPtsInput)}
                    />
                  </td>
                </tr>
                {/* All-In Rate (read-only, derived from Spot + FwdPts) */}
                <tr>
                  <td className="unwind-table-label">{t('modifications.unwind.allInLabel')}</td>
                  <td className="unwind-table-value unwind-table-readonly">
                    {modifiedFields.UnwindRate !== '' ? formatPriceDisplay(Number(modifiedFields.UnwindRate), locale, 4) : '—'}
                  </td>
                </tr>
                {/* Discount checkbox row */}
                <tr>
                  <td colSpan={2}>
                    <label className="unwind-checkbox-label">
                      <input
                        type="checkbox"
                        checked={discountEnabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDiscountEnabled(checked);
                          if (checked) {
                            updateField('UnwindDiscountConvention', '30-day');
                          } else {
                            setUwDiscountRateInput('');
                            // Use updateField for convention — it triggers recalculation.
                            // Also clear discount rate inside modifiedFields first.
                            setModifiedFields(prev => ({ ...prev, UnwindDiscountRate: '' }));
                            updateField('UnwindDiscountConvention', 'None');
                          }
                        }}
                      />
                      {t('modifications.unwind.discountCheckbox')}
                    </label>
                  </td>
                </tr>
                {/* Discount fields — visible only when checkbox is on */}
                {discountEnabled && (
                  <>
                    <tr>
                      <td className="unwind-table-label unwind-table-indent">{t('modifications.fields.unwindDiscountRate')}</td>
                      <td className="unwind-table-value">
                        <div className="unwind-input-with-suffix">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={uwDiscountRateInput}
                            onChange={(e) => setUwDiscountRateInput(e.target.value)}
                            onBlur={handleDiscountRateBlur}
                          />
                          <span className="unwind-input-suffix">%</span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="unwind-table-label unwind-table-indent">{t('modifications.fields.unwindDiscountConvention')}</td>
                      <td className="unwind-table-value">
                        <select value={modifiedFields.UnwindDiscountConvention || '30-day'} onChange={(e) => updateField('UnwindDiscountConvention', e.target.value)}>
                          <option value="30-day">{t('modifications.unwind.convention30day')}</option>
                          <option value="Annual">{t('modifications.unwind.conventionAnnual')}</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td className="unwind-table-label unwind-table-indent">{t('modifications.unwind.residualDays')}</td>
                      <td className="unwind-table-value unwind-table-readonly">{unwindResidualDays}</td>
                    </tr>
                  </>
                )}
                {/* Settlement Amount — last row, editable, with strikethrough when overridden */}
                {modifiedFields.UnwindRate !== '' && modifiedFields.UnwindGrossResult !== '' && (
                  <tr className="unwind-settlement-row">
                    <td className="unwind-table-label">{t('modifications.unwind.settlementLabel')}</td>
                    <td className="unwind-table-value">
                      {/* Strikethrough of calculated value when user has overridden */}
                      {calculatedSettlement !== null && modifiedFields.UnwindSettlementAmount !== calculatedSettlement && (
                        <span className="unwind-calc-strikethrough">
                          {formatAmountDisplay(calculatedSettlement, locale, settlementDecimals)} {trade.Currency2}
                        </span>
                      )}
                      <div className={`unwind-settlement-display ${(modifiedFields.UnwindSettlementAmount ?? 0) >= 0 ? 'unwind-receivable' : 'unwind-payable'}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="unwind-settlement-input"
                          value={uwSettlementInput}
                          onChange={(e) => setUwSettlementInput(e.target.value)}
                          onBlur={handleSettlementBlur}
                        />
                        <span className="unwind-settlement-ccy">{trade.Currency2}</span>
                      </div>
                    </td>
                  </tr>
                )}
                {/* KAN-245: Settlement account dropdown — show only the relevant side */}
                {uwBankAccounts.length > 0 && modifiedFields.UnwindSettlementDirection && (
                  <tr>
                    <td className="unwind-table-label">
                      {modifiedFields.UnwindSettlementDirection === 'Payable'
                        ? t('modifications.unwind.cargarAccount', 'Pagamos en')
                        : t('modifications.unwind.abonarAccount', 'Recibimos en')}
                    </td>
                    <td className="unwind-table-value">
                      <select
                        value={(modifiedFields.UnwindSettlementDirection === 'Payable'
                          ? uwAccountSelections.cargarAccountId
                          : uwAccountSelections.abonarAccountId) || ''}
                        onChange={(e) => {
                          const key = modifiedFields.UnwindSettlementDirection === 'Payable' ? 'cargarAccountId' : 'abonarAccountId';
                          setUwAccountSelections(prev => ({ ...prev, [key]: e.target.value || undefined }));
                        }}
                        style={{ width: '100%', padding: '4px 6px', fontSize: '13px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      >
                        <option value="">{t('modifications.unwind.selectAccount', '— Seleccionar cuenta —')}</option>
                        {uwBankAccounts
                          .filter(a => a.accountCurrency === trade.Currency2)
                          .map(a => (
                            <option key={a.id} value={a.id}>{a.bankName} - {a.accountNumber} ({a.accountCurrency})</option>
                          ))}
                      </select>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            );
          })()}

          {/* Edit fields table (Rollover, Settlement, Grouping) */}
          {fields.length > 0 && (
            <table className="mod-panel-table">
              <thead>
                <tr>
                  <th>{t('modifications.detail.fieldLabel')}</th>
                  <th>{t('modifications.detail.currentValues')}</th>
                  <th>{t('modifications.detail.newValues')}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => {
                  // Hide SettlementCurrency and FixingReference when switching to Entrega Física
                  if (
                    modType === 'SettlementTypeChange' &&
                    (f.key === 'SettlementCurrency' || f.key === 'FixingReference') &&
                    modifiedFields.SettlementType !== 'Compensación'
                  ) {
                    return null;
                  }
                  return (
                    <tr key={f.key}>
                      <td className="mod-panel-field-name">
                        {f.label}
                        {!OPTIONAL_FIELDS.has(f.key) && <span className="mod-panel-required">*</span>}
                      </td>
                      <td className="mod-panel-current">{f.current}</td>
                      <td className="mod-panel-new">{f.input}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}


          {/* Cancellation form */}
          {modType === 'Cancellation' && (
            <div className="mod-panel-cancellation">
              <div className="modifications-warning" style={{ margin: 0 }}>
                <AlertTriangle size={14} className="modifications-warning-icon" />
                <span className="modifications-warning-text">
                  {t('modifications.detail.cancellationWarning')}
                </span>
              </div>
              <textarea
                className="mod-panel-textarea"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t('modifications.detail.cancellationReasonPlaceholder')}
                rows={3}
              />
            </div>
          )}

          {/* Other type info */}
          {modType === 'Other' && (
            <div className="modifications-warning" style={{ margin: 0 }}>
              <AlertTriangle size={14} className="modifications-warning-icon" />
              <span className="modifications-warning-text">
                {t('modifications.detail.otherInfo')}
              </span>
            </div>
          )}
        </>
      )}

      {/* Reject reason — shown instead of form content when rejecting */}
      {showRejectForm && (
        <div className="mod-panel-reject-form">
          <textarea
            className="mod-panel-textarea"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('modifications.detail.rejectReasonPlaceholder')}
            rows={2}
            autoFocus
          />
        </div>
      )}

      {/* Action buttons */}
      {canConfirmReject && (
        <div className="mod-panel-actions">
          {!showRejectForm ? (
            <>
              <button
                className="modifications-btn-reject"
                onClick={() => setShowRejectForm(true)}
                disabled={submitting}
              >
                {t('modifications.detail.rejectBtn')}
              </button>
              <button
                className="modifications-btn-confirm"
                onClick={handleConfirm}
                disabled={submitting || !canSubmitConfirm}
              >
                {submitting ? t('common.loading') : confirmLabel}
              </button>
            </>
          ) : (
            <>
              <button
                className="mod-panel-btn-cancel"
                onClick={() => setShowRejectForm(false)}
                disabled={submitting}
              >
                {t('common.cancel')}
              </button>
              <button
                className="modifications-btn-reject"
                onClick={handleReject}
                disabled={submitting || !rejectReason.trim()}
              >
                {submitting ? t('common.loading') : t('modifications.detail.confirmReject')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ModificationDetailPanel;
